import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Users } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter18 Task 10 — Kiosk view.
 * Read-only dashboard for TV/tablet in the staff area. Polls every 30s.
 * URL: /kiosk
 *
 * No auth — endpoint exposes only aggregate non-PII stats (count + bonus).
 * Physical access control (mounted in a back-of-house TV) is the operational guard.
 */
export default function KioskPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/kiosk/employees-today`);
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load');
    }
  };

  useEffect(() => {
    fetchData();
    const refresh = data?.auto_refresh_seconds || 30;
    const id = setInterval(fetchData, refresh * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employees = data?.employees || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white p-8" data-testid="kiosk-page">
      <header className="flex items-center justify-between mb-8 pb-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FF5A00] flex items-center justify-center">
            <Trophy size={28} weight="fill" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight">AutoSpa5</h1>
            <p className="text-xs uppercase tracking-[0.3em] text-white/45">Ekipi sot · Përditësohet çdo 30s</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-white/55">{new Date().toLocaleDateString('sq-AL', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <p className="text-2xl font-mono font-bold text-[#0ABAB5] tabular-nums" data-testid="kiosk-clock">
            {new Date().toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </header>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      {employees.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-32" data-testid="kiosk-empty">
          <Users size={64} weight="duotone" className="text-white/15 mb-4" />
          <p className="text-2xl font-bold text-white/40">Asnjë punonjës i regjistruar sot</p>
          <p className="text-sm text-white/30 mt-1">Të dhënat shfaqen sa herë regjistrohet një brendësi.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="kiosk-grid">
          {employees.map((emp, idx) => (
            <EmployeeKioskCard key={emp.user_id} emp={emp} rank={idx + 1} />
          ))}
        </div>
      )}

      <footer className="mt-8 text-center text-[10px] uppercase tracking-[0.3em] text-white/25">
        Last update {data?.as_of ? new Date(data.as_of).toLocaleTimeString('sq-AL') : '—'}
      </footer>
    </div>
  );
}

function EmployeeKioskCard({ emp, rank }) {
  const isFirst = rank === 1;
  return (
    <div
      className={`p-6 rounded-3xl border ${
        isFirst
          ? 'bg-gradient-to-br from-[#FFD700]/10 to-transparent border-[#FFD700]/30 shadow-[0_0_30px_rgba(255,215,0,0.15)]'
          : 'bg-[#0a0a0a] border-white/8'
      }`}
      data-testid={`kiosk-card-${emp.user_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">#{rank}</p>
          <h3 className="text-xl font-bold text-white truncate">{emp.name}</h3>
          <p className="text-[10px] uppercase tracking-wider text-white/35 mt-0.5">
            {emp.role === 'location_manager' ? 'Manager' : 'Punonjës'}
          </p>
        </div>
        {isFirst && <span className="text-3xl leading-none" role="img" aria-label="crown">👑</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-black/40">
          <p className="text-[9px] uppercase tracking-wider text-white/45">Brendësi sot</p>
          <p className="text-3xl font-bold text-white tabular-nums" data-testid={`kiosk-count-${emp.user_id}`}>
            {emp.completed_today}
          </p>
        </div>
        <div className="p-3 rounded-xl bg-[#FFD700]/8">
          <p className="text-[9px] uppercase tracking-wider text-[#FFD700]/70">Bonus tani</p>
          <p className="text-3xl font-bold text-[#FFD700] tabular-nums" data-testid={`kiosk-bonus-${emp.user_id}`}>
            +{emp.current_bonus.toLocaleString()}
          </p>
        </div>
      </div>

      {emp.next_target ? (
        <div>
          <div className="flex justify-between text-[10px] text-white/45 mb-1.5">
            <span>Niveli i ardhshëm: {emp.next_target}</span>
            <span>+{emp.next_bonus} Lekë</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#0ABAB5] to-[#FF5A00] transition-all duration-700"
              style={{ width: `${emp.progress_pct}%` }}
              data-testid={`kiosk-progress-${emp.user_id}`}
            />
          </div>
        </div>
      ) : (
        <p className="text-[11px] font-bold text-[#FF5A00]">Niveli maksimal arritur! 🚀</p>
      )}
    </div>
  );
}
